import type { ReactNode } from 'react'
import { Alert, AlertTitle, Box, Dialog, Typography } from '@mui/material'
import logoSrc from '~/assets/logo/logo.svg'
import type { SxProps, Theme } from '@mui/material'

const styles = {
  // Dialog デフォルトの zIndex（modal=1300）のままだと、裏の画面が出す
  // Snackbar（1400）がこのエラー画面の上に重なって矛盾した誘導になるため、
  // snackbar より手前にする。
  dialog: theme => ({
    zIndex: theme.zIndex.snackbar + 1,
  }),
  paper: theme => ({
    backgroundColor: theme.customPalette.bg,
    display: 'flex',
    overflowY: 'auto',
    padding: 4,
  }),
  // Figma（Rump ガイドライン）は見出しと本文の各段落を 600px のボックスに入れ、
  // 間隔をすべて 24px（rowGap: 3）で揃えている。段落を <br /> で繋ぐと支援技術に
  // 1 段落として読まれるため、要素として分けて rowGap で空ける。
  content: {
    width: '100%',
    maxWidth: '600px',
    display: 'grid',
    rowGap: 3,
    // flex の中央寄せ（justify/align center）だとビューポートより縦に長いとき
    // 上端がスクロール到達不能になるため、margin: auto で安全に中央寄せする
    margin: 'auto',
  },
  logo: {
    width: '250px',
    justifySelf: 'center',
    marginBottom: 3,
  },
  action: {
    justifySelf: 'center',
  },
} as const satisfies Record<string, SxProps<Theme>>

type ContentProps = {
  /** 見出し。中央寄せで表示する */
  title: string
  /** 見出し要素の id。KioskErrorDialog の aria-labelledby と結ぶときに渡す */
  titleId?: string
  /** 本文。1 要素 = 1 段落として左寄せで表示する */
  paragraphs: readonly string[]
  /** 再試行失敗などの Alert。無ければ表示しない */
  alert?: ReactNode
  /** 「再試行」「ホームに戻る」等のボタン。中央寄せで表示する */
  action: ReactNode
}

/**
 * キオスク端末（kiosk）向けエラー画面の共通レイアウト。
 * Rump のガイドライン「ロゴ + 見出し + 本文段落 + ボタン」の構成をそのまま実装する。
 * 全画面 Dialog（KioskErrorDialog）の中でも、ページ内（PageLayout の center）でも使える。
 */
export function KioskErrorContent({
  title,
  titleId,
  paragraphs,
  alert,
  action,
}: ContentProps) {
  return (
    <Box sx={styles.content}>
      <Box
        component="img"
        src={logoSrc}
        alt="カミナシ レポート"
        sx={styles.logo}
      />
      <Typography id={titleId} variant="h1" sx={{ textAlign: 'center' }}>
        {title}
      </Typography>
      {paragraphs.map(paragraph => (
        <Typography key={paragraph} variant="body2">
          {paragraph}
        </Typography>
      ))}
      {alert}
      <Box sx={styles.action}>{action}</Box>
    </Box>
  )
}

type DialogProps = {
  /** 中の KioskErrorContent の見出し id。スクリーンリーダーに dialog の名前として読ませる */
  titleId: string
  children: ReactNode
}

/**
 * キオスク端末（kiosk）向けエラー画面を全画面オーバーレイとして表示する Dialog。
 * 背後の画面を操作できないよう、フォーカストラップ・aria-modal・背景の inert 化を持つ
 * MUI Dialog で全面を覆う（キーボードやスクリーンリーダー経由の到達も防ぐ）。
 * 閉じる手段は呼び出し側の再試行成功のみ。
 */
export function KioskErrorDialog({ titleId, children }: DialogProps) {
  return (
    <Dialog
      open
      fullScreen
      disableEscapeKeyDown
      aria-labelledby={titleId}
      sx={styles.dialog}
      slotProps={{ paper: { sx: styles.paper } }}
    >
      {children}
    </Dialog>
  )
}

type RetryFailedAlertProps = {
  message: string
}

/** 再試行に失敗したときの Alert */
export function KioskRetryFailedAlert({ message }: RetryFailedAlertProps) {
  return (
    <Alert severity="error">
      <AlertTitle sx={{ fontWeight: 'bold' }}>再試行に失敗しました</AlertTitle>
      <Typography variant="body2" sx={{ color: 'text.primary' }}>
        {message}
      </Typography>
    </Alert>
  )
}
